import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import moment from "moment";
import { designColors, designType, designWeights } from "services/constants/designTokens";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { Field, FormSection, panelBodySx } from "../Shared/formUi";
import { SelectField, TextField } from "../Shared/fieldInputs";
import { monthsBetween, shortMonth } from "./bonusCalc";
import { cycleLabel } from "./bonusSampleApi";
import {
  DetailRows,
  EntryStatusChips,
  MONTH_OPTIONS,
  MonthTable,
  SectionCard,
  forPeriodLong,
  lastMonth,
  monthNote,
  rupees,
  wagesLabel,
} from "./bonusUi";

const STATUTORY_MANUAL_WARNING = "Manual amounts are not checked against statutory eligibility or calculation.";

const historyTime = (at) => moment(at).format("DD MMM YYYY, HH:mm");

const sourceText = (entry) => {
  if (entry.source === "run") {
    const mode = entry.run_pay_mode === "part" ? `part ${entry.run_part_percent}%` : entry.calc?.already_paid > 0 ? "balance" : "full";
    return `Bonus run ${entry.run_id} · ${mode}`;
  }
  if (entry.source === "import") return `Import${entry.import_ref ? ` · ${entry.import_ref}` : ""}`;
  return "Manual";
};

// Month-wise breakdown of a bonus-run entry (its stored calculation).
export const MonthwiseDialog = ({ open, onClose, title, row, type }) => (
  <Mui.Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
    <Mui.DialogTitle sx={{ ...designType.sectionHeading, fontSize: 17 }}>{title}</Mui.DialogTitle>
    <Mui.DialogContent>
      {row ? (
        <>
          <MonthTable row={row} type={type} />
          <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>{monthNote(row, type)}</Mui.Typography>
        </>
      ) : null}
    </Mui.DialogContent>
    <Mui.DialogActions sx={{ px: 3, pb: 2 }}>
      <SecondaryButton onClick={onClose}>Close</SecondaryButton>
    </Mui.DialogActions>
  </Mui.Dialog>
);

// Bonus Details drawer (spec Figures A8, A10; actions per status, B5).
export const BonusEntryDetailsPanel = ({ entry, types, cycles, busy, canEdit, onClose, onCancelEntry, onAssignCycle, onEdit }) => {
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [cycleId, setCycleId] = useState("");
  const [monthwise, setMonthwise] = useState(false);
  useEffect(() => { setReason(""); setReasonError(""); setCycleId(""); setMonthwise(false); }, [entry?.id]);
  if (!entry) return null;

  const type = types.find((t) => t.id === entry.bonus_type_id);
  const calcType = { ...(type || {}), ...(entry.calc ? { method: entry.calc.method, rate: entry.calc.rate, days_basis: entry.calc.days_basis } : {}) };
  const ready = entry.status === "ready";
  const isRun = entry.source === "run";
  const openCycles = cycles.filter((c) => !c.frozen).map((c) => ({ value: c.id, label: cycleLabel(c) }));
  const calc = entry.calc;

  const cancel = () => {
    if (!reason.trim()) { setReasonError("Enter a reason to cancel."); return; }
    onCancelEntry(entry, reason.trim());
  };

  return (
    <GlobalSidePanel
      open
      onClose={onClose}
      title="Bonus Details"
      subtitle={
        <>
          {entry.employee_name} · {entry.employee_code}{entry.site ? ` · ${entry.site}` : ""} · <EntryStatusChips entry={entry} />
        </>
      }
      bodySx={panelBodySx}
      footer={
        canEdit && ready ? (
          <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
            <SecondaryButton disabled={busy} onClick={cancel} sx={{ color: "#B42318", borderColor: "#FECDD3" }}>Cancel entry</SecondaryButton>
            {!isRun ? <PrimaryButton disabled={busy} onClick={() => onEdit(entry)}>Edit</PrimaryButton> : null}
          </Mui.Stack>
        ) : null
      }
    >
      <Mui.Stack spacing={2}>
        <SectionCard title="Bonus">
          <DetailRows
            rows={[
              { label: "Bonus type", value: entry.bonus_type_name },
              { label: "For period", value: forPeriodLong(entry.period_from, entry.period_to) },
              !isRun ? { label: "Amount", value: rupees(entry.amount) } : null,
              { label: "Payroll cycle", value: entry.payroll_cycle ? cycleLabel(entry.payroll_cycle) : entry.status === "cancelled" ? "—" : "Not assigned (Deferred)", color: entry.deferred ? "#C2410C" : undefined },
              { label: "Source", value: sourceText(entry) },
              entry.remarks ? { label: "Remarks", value: entry.remarks } : null,
              entry.cancel_reason ? { label: "Cancel reason", value: entry.cancel_reason } : null,
            ]}
          />
        </SectionCard>

        {!isRun && entry.is_statutory ? <RequestInfoBanner tone="warning" body={STATUTORY_MANUAL_WARNING} /> : null}

        {isRun && calc ? (
          <SectionCard title="Summary">
            <DetailRows
              rows={[
                { label: "Months counted", value: calc.months_counted },
                calc.wages_used_total != null ? { label: `Wages used${calcType.method === "percent_of_wages" ? " (capped)" : ""}`, value: rupees(calc.wages_used_total) } : null,
                calc.days_counted_total != null && calcType.method === "per_day" ? { label: calcType.days_basis === "days_present" ? "Days present" : "Paid days", value: calc.days_counted_total } : null,
                { label: `Bonus due${calcType.method === "percent_of_wages" ? ` (${Number(calcType.rate)}%)` : ""}`, value: rupees(calc.due) },
                { label: "Earlier bonus", value: rupees(calc.already_paid) },
                { label: "Paid in salary", value: rupees(calc.paid_in_salary), indent: true },
                { label: "Waiting in payroll", value: rupees(calc.waiting_in_payroll), indent: true },
                { label: `Paying now (${entry.run_pay_mode === "part" ? `part ${entry.run_part_percent}%` : "balance"})`, value: rupees(entry.amount), color: "#067647" },
                { label: "Left to pay", value: rupees(Math.max(0, calc.due - calc.already_paid - entry.amount)) },
              ]}
            />
            <Mui.Link component="button" type="button" onClick={() => setMonthwise(true)} sx={{ mt: 1.5, fontWeight: designWeights.semibold, color: designColors.tealDark, fontSize: 14 }}>
              View month-wise breakdown
            </Mui.Link>
          </SectionCard>
        ) : null}

        {canEdit && entry.deferred ? (
          <SectionCard title="Assign payroll cycle" tone="green">
            <Field label="Payroll cycle *" error={undefined} wide>
              <SelectField value={cycleId} onChange={setCycleId} options={openCycles} placeholder="Select payroll cycle" />
            </Field>
            <Mui.Typography sx={{ ...designType.captionText, mt: 1 }}>
              Only open (not frozen) cycles are shown. The entry stays Ready and is picked up by that payroll.
            </Mui.Typography>
            <PrimaryButton sx={{ mt: 1.5 }} disabled={busy || !cycleId} onClick={() => onAssignCycle(entry, cycleId)}>Assign</PrimaryButton>
          </SectionCard>
        ) : null}

        <SectionCard title="History">
          <Mui.Stack spacing={1}>
            {(entry.history || []).map((item, i) => (
              <Mui.Typography key={i} sx={designType.tableBodyCell}>
                {historyTime(item.at)} · <b>{item.by}</b> {item.text}
              </Mui.Typography>
            ))}
          </Mui.Stack>
        </SectionCard>

        {ready && isRun ? (
          <Mui.Typography sx={designType.captionText}>
            Entries from a bonus run cannot be edited. To correct one, cancel it and add a corrected bonus.
          </Mui.Typography>
        ) : null}
        {entry.status === "included" || entry.status === "processed" ? (
          <RequestInfoBanner body={`${entry.status === "processed" ? "Paid in salary" : "Included in a payroll run"} — read-only. Corrections go through Payroll Adjustments.`} />
        ) : null}
        {canEdit && ready ? (
          <Field label="Reason (needed to cancel)" error={reasonError} wide>
            <TextField value={reason} onChange={(value) => { setReason(value.slice(0, 255)); setReasonError(""); }} placeholder="Type a reason" error={reasonError} />
          </Field>
        ) : null}
      </Mui.Stack>

      <MonthwiseDialog
        open={monthwise}
        onClose={() => setMonthwise(false)}
        title={`${entry.employee_name} · ${entry.bonus_type_name} · ${forPeriodLong(entry.period_from, entry.period_to)}`}
        row={calc}
        type={calcType}
      />
    </GlobalSidePanel>
  );
};

// + Add Bonus / Edit (manual or imported, Ready only). Spec B9: any active
// bonus type; amount > 0 with at most 2 decimals; payroll cycle pre-filled
// from the default treatment.
export const BonusEntryFormPanel = ({ entry, employees, types, cycles, treatment, saving, serverErrors, onClose, onSave }) => {
  const openCycles = cycles.filter((c) => !c.frozen);
  const currentCycle = openCycles[0];
  const defaultCycle = treatment === "CURRENT_CYCLE" ? currentCycle?.id ?? "" : "";
  const [form, setForm] = useState(() => (entry
    ? {
      employee_id: entry.employee_id,
      bonus_type_id: entry.bonus_type_id,
      period_from: entry.period_from,
      period_to: entry.period_to,
      amount: String(entry.amount),
      payroll_cycle_id: entry.payroll_cycle_id ?? "",
      remarks: entry.remarks || "",
    }
    : { employee_id: "", bonus_type_id: "", period_from: lastMonth(), period_to: lastMonth(), amount: "", payroll_cycle_id: defaultCycle, remarks: "" }));
  const [submitted, setSubmitted] = useState(false);
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));

  const activeTypes = types.filter((t) => t.status !== "Inactive" || t.id === entry?.bonus_type_id);
  const type = types.find((t) => t.id === Number(form.bonus_type_id));
  const errors = useMemo(() => {
    const result = {};
    if (!form.employee_id) result.employee_id = "Select the employee.";
    if (!form.bonus_type_id) result.bonus_type_id = "Select the bonus type.";
    if (!form.period_from) result.period_from = "Select Period from.";
    if (!form.period_to) result.period_to = "Select Period to.";
    else if (form.period_from && form.period_from > form.period_to) result.period_to = "Period to must be the same as or after Period from.";
    const amount = String(form.amount).trim();
    if (!amount) result.amount = "Enter the amount.";
    else if (!/^-?\d+(\.\d+)?$/.test(amount) || Number(amount) <= 0) result.amount = "Amount must be more than 0.";
    else if (!/^\d+(\.\d{1,2})?$/.test(amount)) result.amount = "Amount can have at most 2 decimals.";
    if (treatment === "SELECT_CYCLE" && !form.payroll_cycle_id) result.payroll_cycle_id = "Payroll cycle required.";
    return result;
  }, [form, treatment]);
  const shown = { ...(serverErrors || {}), ...(submitted ? errors : {}) };

  const cycleOptions = [
    ...(treatment === "ON_HOLD" || !form.payroll_cycle_id || entry?.payroll_cycle_id == null ? [{ value: "", label: "Defer — assign a cycle later" }] : []),
    ...openCycles.map((c) => ({ value: c.id, label: cycleLabel(c) })),
  ];
  const months = form.period_from && form.period_to && form.period_from <= form.period_to ? monthsBetween(form.period_from, form.period_to) : [];

  const save = () => {
    setSubmitted(true);
    if (Object.keys(errors).length || saving) return;
    onSave(form);
  };

  return (
    <GlobalSidePanel
      open
      onClose={() => { if (!saving) onClose(); }}
      title={entry ? "Edit Bonus" : "Add Bonus"}
      subtitle={entry ? `${entry.employee_name} · ${entry.bonus_type_name}` : "Type in a bonus for one person. It is saved as Ready."}
      bodySx={panelBodySx}
      footer={
        <PanelActionFooter
          onCancel={() => { if (!saving) onClose(); }}
          primaryLabel={saving ? "Saving..." : entry ? "Save" : "Add bonus"}
          primaryProps={{ onClick: save, disabled: saving }}
        />
      }
    >
      <Mui.Stack spacing={2}>
        <FormSection title="Bonus">
          <Field label="Employee *" error={shown.employee_id} wide>
            <SelectField
              value={form.employee_id}
              onChange={(value) => set("employee_id")(value === "" ? "" : Number(value))}
              options={employees.map((e) => ({ value: e.id, label: `${e.name} (${e.code})` }))}
              placeholder="Select employee"
              error={shown.employee_id}
            />
          </Field>
          <Field label="Bonus type *" error={shown.bonus_type_id} wide>
            <SelectField
              value={form.bonus_type_id}
              onChange={(value) => set("bonus_type_id")(value === "" ? "" : Number(value))}
              options={activeTypes.map((t) => ({ value: t.id, label: t.name }))}
              placeholder={activeTypes.length ? "Select bonus type" : "No active bonus types — activate one in Bonus settings"}
              error={shown.bonus_type_id}
            />
          </Field>
          <Field label="Period from *" error={shown.period_from}>
            <SelectField value={form.period_from} onChange={set("period_from")} options={MONTH_OPTIONS} placeholder="Month" error={shown.period_from} />
          </Field>
          <Field label="Period to *" error={shown.period_to}>
            <SelectField value={form.period_to} onChange={set("period_to")} options={MONTH_OPTIONS} placeholder="Month" error={shown.period_to} />
          </Field>
          <Field label="Amount (₹) *" helper="More than 0, at most 2 decimals" error={shown.amount}>
            <TextField type="number" value={form.amount} onChange={set("amount")} placeholder="e.g. 5000" error={shown.amount} />
          </Field>
          <Field label={`Payroll cycle${treatment === "SELECT_CYCLE" ? " *" : ""}`} helper="Open cycles only" error={shown.payroll_cycle_id}>
            <SelectField
              value={form.payroll_cycle_id}
              onChange={(value) => set("payroll_cycle_id")(value === "" ? "" : Number(value))}
              options={cycleOptions}
              placeholder="Select payroll cycle"
              error={shown.payroll_cycle_id}
            />
          </Field>
          <Field label="Remarks" wide>
            <TextField type="multiline" value={form.remarks} onChange={(value) => set("remarks")(value.slice(0, 255))} placeholder="Optional" />
          </Field>
        </FormSection>
        {type?.is_statutory ? <RequestInfoBanner tone="warning" body={STATUTORY_MANUAL_WARNING} /> : null}
        {months.length > 1 && Number(form.amount) > 0 ? (
          <Mui.Typography sx={designType.captionText}>
            For earlier-bonus tracking the amount is split evenly over {months.length} months ({shortMonth(months[0])} – {shortMonth(months[months.length - 1])}). This does not say when it was earned.
          </Mui.Typography>
        ) : null}
        {type && type.method !== "enter_amount" ? (
          <Mui.Typography sx={designType.captionText}>
            Source is saved as Manual — it is never shown as calculated.{type.method === "percent_of_wages" ? ` Wages used for this type: ${wagesLabel(type)}.` : ""}
          </Mui.Typography>
        ) : null}
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

